// The slider position (0–1000) maps on a log scale to a monthly query volume;
// the price is that volume at the live GeoIP Insights per-query rate, falling
// back to a default rate if the live price can't be fetched.
const MIN_QUERIES = 10_000;
const MAX_QUERIES = 1_250_000;
const FALLBACK_PRICE_PER_QUERY = 0.002;

interface ServicePricing {
  name: string;
  price: string;
}

const roundQueries = (n: number): number => {
  if (n < 10_000) {
    return Math.round(n / 500) * 500;
  }
  if (n < 100_000) {
    return Math.round(n / 1_000) * 1_000;
  }
  return Math.round(n / 5_000) * 5_000;
};

const numberFormat = new Intl.NumberFormat('en-US');
const formatNumber = (n: number): string => numberFormat.format(n);

const fetchGeoipPricePerQuery = async (): Promise<number | null> => {
  try {
    const response = await fetch('/en/shopping-cart/dollars-to-queries/100');
    if (!response.ok) {
      console.error('Failed to fetch GeoIP Insights pricing');
      return null;
    }

    const services = (await response.json()) as ServicePricing[];
    const geoip = services.find((s) => s.name === 'geoip2-precision-insights');
    if (!geoip) {
      console.error('GeoIP Insights pricing not found in response');
      return null;
    }

    const price = parseFloat(geoip.price.replace('$', ''));
    return Number.isFinite(price) ? price : null;
  } catch (error) {
    console.error('Error fetching GeoIP Insights pricing:', error);
    return null;
  }
};

const initCostEstimator = (): void => {
  const slider = document.querySelector<HTMLInputElement>('.js-cost-slider');
  const queriesEl = document.querySelector<HTMLElement>('.js-cost-queries');
  const priceEl = document.querySelector<HTMLElement>('.js-cost-price');

  if (!slider || !queriesEl || !priceEl) {
    return;
  }

  let pricePerQuery = FALLBACK_PRICE_PER_QUERY;

  const update = (): void => {
    const t = parseInt(slider.value, 10) / 1_000;
    const queries = roundQueries(
      Math.round(MIN_QUERIES * (MAX_QUERIES / MIN_QUERIES) ** t),
    );
    const price = Math.round(queries * pricePerQuery);
    const formattedQueries = formatNumber(queries);

    slider.style.setProperty('--value-percent', `${t * 100}%`);
    slider.setAttribute('aria-valuetext', formattedQueries);
    queriesEl.textContent = formattedQueries;
    priceEl.textContent = `$${formatNumber(price)}/mo`;
  };

  update();
  slider.addEventListener('input', update);

  // Refine the estimate with the live per-query rate once it loads.
  void fetchGeoipPricePerQuery().then((livePrice) => {
    if (livePrice !== null) {
      pricePerQuery = livePrice;
      update();
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCostEstimator);
} else {
  initCostEstimator();
}
